import { useEffect } from "react";
import { createRoot } from "react-dom/client";
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import NotFound from "./components/NotFound";
import About from "./pages/About";
import Dashboard from "./pages/Dashboard";
import Home from "./pages/Home";
import Pricing from "./pages/Pricing";
import Service from "./pages/Service";
import Templates from "./pages/Templates";
import TemplateProjectDetail from "./pages/Templates/Detail";
import WebsiteLibraryPage from "./features/WebsiteLibrary/Component";
import { websiteProjects } from "./features/WebsiteLibrary/projects";
import DemoComponent from "./pages/Templates/components/demo";
import "./index.css";
import "./site.css";
import "./studio.css";

const titles: Record<string, string> = {
  "/": "CMJ Software | Strategy, Design & Engineering",
  "/about": "About | CMJ Software",
  "/services": "Software Services | CMJ Software",
  "/templates": "Templates as a Service | CMJ Software",
  "/websites": "Explore Websites | CMJ Software",
  "/pricing": "Pricing & Engagement | CMJ Software",
  "/dashboard": "Client Workspace Preview | CMJ Software",
};

const RouteManager = () => {
  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname.replace(/\/+$/, "") || "/";
    const slug = pathname.match(/^\/(?:websites|templates)\/([^/]+)$/)?.[1];
    const project = slug ? websiteProjects.find((item) => item.slug === slug) : undefined;
    const title = project ? `${project.name} Website | CMJ Software` : titles[pathname] || "CMJ Software";
    const description = project
      ? project.summary
      : pathname === "/websites"
        ? "Explore real websites by CMJ Software. Browse distinct page designs, mobile layouts, and dashboard previews, then start a website built around your business."
        : pathname === "/templates"
          ? "Get a custom website with a personal content dashboard and unlimited self-service editing. Explore real projects and choose a build path with CMJ Software."
          : "CMJ Software designs and builds clear, dependable web platforms, mobile apps, and custom business systems.";
    const canonicalPath = project ? `/websites/${project.slug}` : pathname;
    const canonicalUrl = `https://cmjsoftware.com${canonicalPath}`;
    const imageUrl = project?.screens[0]?.src
      ? new URL(project.screens[0].src, "https://cmjsoftware.com").href
      : "https://cmj-site.nyc3.digitaloceanspaces.com/cmjs-bg-5.png";
    document.title = title;
    const setMeta = (attribute: "name" | "property", key: string, content: string) => {
      let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }
      element.content = content;
    };
    setMeta("name", "description", description);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", canonicalUrl);
    setMeta("property", "og:image", imageUrl);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", imageUrl);
    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    if (location.hash) {
      window.requestAnimationFrame(() => {
        document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: "smooth" });
      });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [location.pathname, location.hash]);

  return null;
};

const App = () => (
  <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
    <RouteManager />
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/about" element={<About />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/templates" element={<Templates />} />
      <Route path="/templates/:projectSlug" element={<TemplateProjectDetail />} />
      <Route path="/websites" element={<WebsiteLibraryPage />} />
      <Route path="/websites/:projectSlug" element={<WebsiteLibraryPage />} />
      <Route path="/services" element={<Service />} />
      <Route path="/demo/:page" element={<DemoComponent />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  </BrowserRouter>
);

const container = document.querySelector("#root");
if (!container) throw new Error("Root element not found");

createRoot(container).render(<App />);
